import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { poiApi, type PoiDetail, type PoiCategory } from '../lib/api';
import { useApp } from '../store';

// ─── Icônes emoji par catégorie ──────────────────────────────────────────────
export const POI_EMOJI: Record<string, string> = {
  science:      '🔬',
  culture:      '🏛️',
  economy:      '⚓',
  military:     '⚔️',
  spirituality: '⛪',
};

export const POI_COLOR: Record<string, string> = {
  science:      '#4ade80',  // vert
  culture:      '#f59e0b',  // ambre
  economy:      '#60a5fa',  // bleu
  military:     '#f87171',  // rouge
  spirituality: '#c084fc',  // violet
};

/** Libellés FR — composants non migrés ; préférer i18n dans PoiPanel. */
export const POI_LABEL: Record<string, string> = {
  science:      'Science',
  culture:      'Culture',
  economy:      'Économie',
  military:     'Militaire',
  spirituality: 'Spiritualité',
};

export const POI_LEVEL_LABEL: Record<string, string> = {
  local:    'Local',
  regional: 'Régional',
  national: 'National',
  world:    'Mondial',
};

function usePoiLabels() {
  const { t } = useTranslation(['panels', 'common']);
  const categoryLabel = (cat: string) =>
    t(`common:resources.${cat}`, { defaultValue: cat });
  const levelLabel = (level: string) =>
    t(`panels:poi.levels.${level}`, { defaultValue: level });
  return { t, categoryLabel, levelLabel };
}

// ─── Panel de détail d'un POI ─────────────────────────────────────────────────
export function PoiPanel({ poiId, onClose }: { poiId: number; onClose: () => void }) {
  const { t, categoryLabel, levelLabel } = usePoiLabels();
  const { flyTo } = useApp();
  const { data, isLoading, error } = useQuery({
    queryKey: ['poi', poiId],
    queryFn: () => poiApi.get(poiId),
    staleTime: 60_000,
  });

  const poi: PoiDetail | undefined = data?.poi;
  const emoji = poi ? (POI_EMOJI[poi.category] ?? '📍') : '📍';
  const color = poi ? (POI_COLOR[poi.category] ?? '#c4a35a') : '#c4a35a';

  const goToTerritory = () => {
    if (!poi?.territory) return;
    flyTo({ lat: poi.lat, lng: poi.lng, zoom: 13, territoryId: poi.territory.id, openTerritory: true });
    onClose();
  };

  return (
    <div className="flex h-full flex-col">
      {/* En-tête */}
      <div className="flex items-center gap-3 border-b border-white/10 p-4">
        <span className="text-2xl">{emoji}</span>
        <div className="flex-1 min-w-0">
          {isLoading && <div className="text-sm text-gray-400">{t('poi.loading')}</div>}
          {error   && <div className="text-sm text-red-400">{t('poi.loadError')}</div>}
          {poi && (
            <>
              <div className="font-semibold text-white truncate">{poi.name}</div>
              {poi.nameLocal && poi.nameLocal !== poi.name && (
                <div className="text-xs text-gray-400 truncate">{poi.nameLocal}</div>
              )}
            </>
          )}
        </div>
        <button onClick={onClose} className="shrink-0 text-gray-400 hover:text-white text-lg ml-1">✕</button>
      </div>

      {/* Corps */}
      {poi && (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Badges catégorie + niveau */}
          <div className="flex flex-wrap gap-2">
            <span
              className="px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{ background: color + '33', color, border: `1px solid ${color}55` }}
            >
              {emoji} {categoryLabel(poi.category)}
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs bg-white/10 text-gray-300 border border-white/10">
              {levelLabel(poi.level)}
            </span>
            {poi.isValidated && (
              <span className="px-2.5 py-0.5 rounded-full text-xs bg-green-900/40 text-green-400 border border-green-700/40">
                {t('poi.validated')}
              </span>
            )}
          </div>

          <div className="rounded-lg bg-white/5 border border-white/10 p-3 space-y-1">
            <div className="text-xs text-gray-400 uppercase tracking-wide">{t('poi.baseProduction')}</div>
            <div className="text-lg font-bold text-white">
              +{poi.baseYield.toFixed(1)}
              <span className="text-sm font-normal text-gray-400 ml-1">
                {t('poi.perHour', {
                  resource: categoryLabel(poi.category) || t('poi.resourceFallback'),
                })}
              </span>
            </div>
          </div>

          {/* Territoire rattaché */}
          {poi.territory ? (
            <div className="rounded-lg bg-white/5 border border-white/10 p-3 space-y-2">
              <div className="text-xs text-gray-400 uppercase tracking-wide">{t('poi.territory')}</div>
              <div className="text-sm text-white">{poi.territory.name}</div>
              {poi.territory.department && (
                <div className="text-xs text-gray-500">{poi.territory.department}</div>
              )}
              <button
                onClick={goToTerritory}
                className="w-full mt-1 rounded bg-white/10 hover:bg-white/20 text-xs text-gray-200 py-1.5 transition-colors"
              >
                {t('poi.seeTerritory')}
              </button>
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">{t('poi.noTerritory')}</div>
          )}

          {/* Source */}
          <div className="text-xs text-gray-600">
            {t('poi.source', {
              source: poi.source === 'wikidata' ? t('poi.wikidata') : poi.source,
            })}
            {poi.externalId && (
              <>
                {' · '}
                <a
                  href={`https://www.wikidata.org/wiki/${poi.externalId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-400"
                >
                  {poi.externalId}
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filtre de catégories (barre latérale carte) ──────────────────────────────
export function PoiCategoryFilter({
  active,
  onChange,
}: {
  active: Set<PoiCategory>;
  onChange: (s: Set<PoiCategory>) => void;
}) {
  const { categoryLabel } = usePoiLabels();
  const categories: PoiCategory[] = ['science', 'culture', 'economy', 'military', 'spirituality'];

  const toggle = (cat: PoiCategory) => {
    const next = new Set(active);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    onChange(next);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((cat) => {
        const on = active.has(cat);
        return (
          <button
            key={cat}
            onClick={() => toggle(cat)}
            title={categoryLabel(cat)}
            className={`text-base transition-all rounded p-0.5 border ${
              on
                ? 'opacity-100 border-transparent'
                : 'opacity-30 border-transparent grayscale'
            }`}
            style={on ? { background: POI_COLOR[cat] + '22' } : {}}
          >
            {POI_EMOJI[cat]}
          </button>
        );
      })}
    </div>
  );
}
